// ============================================================================
// CAMPAÑAS (C5-D3) — la pantalla más peligrosa del panel.
//
// Un clic de más aquí manda un mensaje no pedido a cientos de personas, y eso
// no se deshace: WhatsApp puede bloquear el número y el negocio se queda sin su
// canal de ventas. Por eso la UI está diseñada para FRENAR, no para agilizar:
//
//  · el conteo que se muestra es el REAL (ya sin quienes no dieron permiso),
//    calculado contra la base, no una estimación;
//  · confirmar es un paso aparte, con el número escrito en la frase;
//  · solo el dueño ve el botón de aprobar (y la base lo verifica igual);
//  · una plantilla que Meta no ha aprobado no deja ni intentarlo.
//
// La base impide el desastre (I-C5-1 e I-C5-2). Esta pantalla existe para que
// nadie llegue por accidente hasta ahí.
// ============================================================================
import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Megaphone, Send, ShieldCheck, Users } from "lucide-react";
import { Aviso, Boton, Chip, SkeletonLista, Vacio } from "./piezas.tsx";
import {
  aprobarCampania, contarDestinatarios, crearCampania, explicarError, listarCampanias,
  listarPlantillas, listarSegmentos, resultadosDeCampanias, type ResultadoCampania,
} from "./datos.ts";
import type { Campania, ContextoPanel, Plantilla, Segmento } from "./tipos.ts";

const TONO_ESTADO: Record<Campania["estado"], "neutro" | "marca" | "ok" | "alerta"> = {
  borrador: "neutro", aprobada: "marca", programada: "marca", enviando: "marca",
  pausada: "alerta", hecha: "ok", abortada: "alerta",
};

const TEXTO_ESTADO: Record<Campania["estado"], string> = {
  borrador: "Sin aprobar", aprobada: "Aprobada", programada: "Programada",
  enviando: "Enviando", pausada: "Pausada", hecha: "Terminada", abortada: "Cancelada",
};

/**
 * EL RESULTADO DE LA CAMPAÑA, en la línea de siempre.
 *
 * Hasta el 5-ago-2026 esta pantalla solo podía decir en qué estado estaba la
 * campaña («Terminada»), y «terminada» significa **«ya no queda cola»**: una
 * campaña que no le llegó a nadie se ve exactamente igual que una que llegó
 * entera. Los acuses de Meta ya se guardan (migr 0029) — faltaba enseñarlos.
 *
 * Los números son ACUMULATIVOS y así se leen: quien contestó también llegó.
 * Por eso se escriben «12 de 14 llegaron» y no como estados sueltos.
 */
function Resultado({ r }: { r: ResultadoCampania }) {
  if (r.total === 0) return null;
  const cifras = [
    { n: r.salieron, texto: `de ${r.total} salieron` },
    { n: r.llegaron, texto: "llegaron" },
    { n: r.leyeron, texto: "lo leyeron" },
    { n: r.contestaron, texto: "contestaron" },
  ];
  return (
    <div className="flex items-center gap-x-2.5 gap-y-1 flex-wrap mt-2 text-[12.5px] text-tinta-2">
      {cifras.map((c, i) => (
        // El «·» va PEGADO a su cifra, no suelto entre dos: en 320 px la línea
        // se parte y un separador suelto se queda colgando al final del renglón.
        <span key={c.texto}>
          {i > 0 && <span aria-hidden="true" className="mr-2.5">·</span>}
          <strong className="text-tinta font-medium">{c.n}</strong> {c.texto}
        </span>
      ))}
      {r.rebotaron > 0 && (
        <Chip tono="alerta">
          <AlertTriangle size={11} /> {r.rebotaron} rebotaron
        </Chip>
      )}
    </div>
  );
}

function Nueva({
  ctx, plantillas, segmentos, onCreada,
}: {
  ctx: ContextoPanel; plantillas: Plantilla[]; segmentos: Segmento[];
  onCreada: () => void;
}) {
  const [nombre, setNombre] = useState("");
  const [plantillaId, setPlantillaId] = useState("");
  const [segmentoId, setSegmentoId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  // Solo se ofrecen plantillas que Meta ya aprobó: con una en revisión, el envío
  // falla destinatario por destinatario y cada fallo pesa contra el número.
  const usables = plantillas.filter((p) => p.estado_meta === "aprobada");

  async function crear() {
    setGuardando(true);
    try {
      await crearCampania(ctx.sb, ctx.tenantId, nombre.trim(), plantillaId, segmentoId);
      setNombre(""); setPlantillaId(""); setSegmentoId("");
      onCreada();
    } catch (e) {
      setError(explicarError(e));
    } finally {
      setGuardando(false);
    }
  }

  if (usables.length === 0 || segmentos.length === 0) {
    return (
      <Aviso tono="alerta">
        {usables.length === 0
          ? "Todavía no hay plantillas aprobadas por Meta. Sin una plantilla aprobada no se puede iniciar una conversación."
          : "Falta crear al menos un grupo de destinatarios."}
      </Aviso>
    );
  }

  return (
    <div className="border border-borde rounded-xl p-4 bg-superficie space-y-3">
      <h2 className="text-[15px] font-medium">Nueva campaña</h2>
      {error && <Aviso tono="peligro" onCerrar={() => setError(null)}>{error}</Aviso>}
      <div className="grid sm:grid-cols-3 gap-2.5">
        <label className="text-[13px] space-y-1">
          <span className="text-tinta-2">Nombre</span>
          <input value={nombre} onChange={(e) => setNombre(e.target.value)}
            placeholder="Promo de agosto"
            className="w-full h-10 px-3 rounded-lg bg-elevado border border-borde text-[14px]
              focus:outline-none focus:border-marca/50" />
        </label>
        <label className="text-[13px] space-y-1">
          <span className="text-tinta-2">Plantilla aprobada</span>
          <select value={plantillaId} onChange={(e) => setPlantillaId(e.target.value)}
            className="w-full h-10 px-2 rounded-lg bg-elevado border border-borde text-[14px]
              focus:outline-none focus:border-marca/50 cursor-pointer">
            <option value="">Elige…</option>
            {usables.map((p) => <option key={p.id} value={p.id}>{p.nombre_meta}</option>)}
          </select>
        </label>
        <label className="text-[13px] space-y-1">
          <span className="text-tinta-2">A quién</span>
          <select value={segmentoId} onChange={(e) => setSegmentoId(e.target.value)}
            className="w-full h-10 px-2 rounded-lg bg-elevado border border-borde text-[14px]
              focus:outline-none focus:border-marca/50 cursor-pointer">
            <option value="">Elige…</option>
            {segmentos.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
          </select>
        </label>
      </div>
      <Boton tono="marca" disabled={!nombre.trim() || !plantillaId || !segmentoId || guardando}
        onClick={crear}>
        Crear sin enviar
      </Boton>
      <p className="text-[12.5px] text-tinta-2">
        Se crea sin aprobar: nada sale hasta que la revises y confirmes.
      </p>
    </div>
  );
}

/** El paso que frena: enseña el número REAL y obliga a un segundo clic. */
function Confirmar({
  ctx, campania, onHecho, onCancelar,
}: { ctx: ContextoPanel; campania: Campania; onHecho: () => void; onCancelar: () => void }) {
  const [cuantos, setCuantos] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    let vivo = true;
    contarDestinatarios(ctx.sb, campania.segmento_id)
      .then((n) => { if (vivo) setCuantos(n); })
      .catch((e) => { if (vivo) setError(explicarError(e)); });
    return () => { vivo = false; };
  }, [ctx.sb, campania.segmento_id]);

  async function aprobar() {
    setEnviando(true);
    try {
      await aprobarCampania(ctx.sb, campania.id);
      onHecho();
    } catch (e) {
      setError(explicarError(e));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="border border-marca/40 rounded-xl p-4 bg-superficie space-y-3">
      <div className="flex items-center gap-2">
        <AlertTriangle size={16} className="text-marca" />
        <h3 className="text-[15px] font-medium">Confirmar «{campania.nombre}»</h3>
      </div>

      {error && <Aviso tono="peligro" onCerrar={() => setError(null)}>{error}</Aviso>}

      {cuantos === null ? (
        <div className="skeleton h-5 w-48 rounded" />
      ) : (
        <>
          <p className="text-[14px]">
            Se le va a escribir a <strong className="cifra">{cuantos}</strong>{" "}
            {cuantos === 1 ? "persona" : "personas"}.
          </p>
          <p className="text-[12.5px] text-tinta-2 flex items-start gap-1.5">
            <ShieldCheck size={13} className="mt-0.5 shrink-0" />
            Ya quedaron por fuera quienes no dieron permiso o pidieron no recibir mensajes.
            Esto no se puede deshacer una vez empiece.
          </p>
          <div className="flex gap-2">
            <Boton tono="marca" onClick={aprobar} disabled={enviando || cuantos === 0}>
              <Send size={15} /> {enviando ? "Aprobando…" : `Sí, enviar a ${cuantos}`}
            </Boton>
            <Boton onClick={onCancelar}>Mejor no</Boton>
          </div>
          {cuantos === 0 && (
            <p className="text-[12.5px] text-tinta-2">
              Nadie en ese grupo dio permiso para recibir mensajes, así que no hay a quién escribirle.
            </p>
          )}
        </>
      )}
    </div>
  );
}

export function Campanias({ ctx }: { ctx: ContextoPanel }) {
  const { sb, tenantId, rol } = ctx;
  const [lista, setLista] = useState<Campania[]>([]);
  const [plantillas, setPlantillas] = useState<Plantilla[]>([]);
  const [segmentos, setSegmentos] = useState<Segmento[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState<Campania | null>(null);
  const [resultados, setResultados] = useState<Map<string, ResultadoCampania>>(new Map());

  const esDueno = rol === "dueno" || rol === "victorix";

  const recargar = useCallback(async () => {
    try {
      const [c, p, s] = await Promise.all([
        listarCampanias(sb, tenantId),
        listarPlantillas(sb, tenantId),
        listarSegmentos(sb, tenantId),
      ]);
      // Solo las que ya salieron: una en borrador no tiene lista congelada
      // todavía, así que preguntar por sus envíos siempre daría cero.
      const res = await resultadosDeCampanias(
        sb, c.filter((x) => x.estado !== "borrador").map((x) => x.id));
      setLista(c); setPlantillas(p); setSegmentos(s); setResultados(res); setError(null);
    } catch (e) {
      setError(explicarError(e));
    } finally {
      setCargando(false);
    }
  }, [sb, tenantId]);

  useEffect(() => { void recargar(); }, [recargar]);

  return (
    <div className="h-full flex flex-col min-w-0">
      <header className="shrink-0 px-4 sm:px-6 pt-4 pb-3 border-b border-borde-suave">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-[19px] font-semibold tracking-tight">Campañas</h1>
          <Chip tono="neutro"><Megaphone size={11} /> {lista.length}</Chip>
        </div>
        <p className="text-[13px] text-tinta-2 mt-0.5">
          Mensajes que salen sin que el cliente haya escrito primero. Solo a quien dio permiso.
        </p>
      </header>

      <div className="flex-1 overflow-y-auto scroll-fino px-4 sm:px-6 py-4">
        <div className="max-w-[820px] mx-auto w-full space-y-4">
          {error && <Aviso tono="peligro" onCerrar={() => setError(null)}>{error}</Aviso>}
          {cargando && <SkeletonLista filas={3} />}

          {!cargando && esDueno && (
            <Nueva ctx={ctx} plantillas={plantillas} segmentos={segmentos}
              onCreada={() => { void recargar(); }} />
          )}

          {confirmando && (
            <Confirmar ctx={ctx} campania={confirmando}
              onCancelar={() => setConfirmando(null)}
              onHecho={() => { setConfirmando(null); void recargar(); }} />
          )}

          {!cargando && lista.length === 0 && (
            <Vacio titulo="Todavía no hay campañas">
              Una campaña sirve para escribirle a varios clientes a la vez — por ejemplo, avisar de
              una promoción. Solo le llega a quien dio permiso.
            </Vacio>
          )}

          {lista.length > 0 && (
            <ul className="space-y-2">
              {lista.map((c) => (
                <li key={c.id} className="border border-borde rounded-xl p-3.5 bg-superficie">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-[14.5px]">{c.nombre}</span>
                    <Chip tono={TONO_ESTADO[c.estado]}>{TEXTO_ESTADO[c.estado]}</Chip>
                    {c.estado === "borrador" && esDueno && (
                      <Boton tono="marca" className="ml-auto h-8 text-[13px]"
                        onClick={() => setConfirmando(c)}>
                        <Users size={14} /> Revisar y enviar
                      </Boton>
                    )}
                  </div>
                  {c.motivo_pausa && (
                    <p className="text-[12.5px] text-tinta-2 mt-1.5">
                      Se detuvo sola: {c.motivo_pausa}
                    </p>
                  )}
                  {resultados.has(c.id) && <Resultado r={resultados.get(c.id)!} />}
                  {c.estado === "borrador" && !esDueno && (
                    <p className="text-[12.5px] text-tinta-2 mt-1.5">
                      Solo el dueño puede aprobar el envío.
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
